import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { BookOpen, RefreshCw, CheckCircle } from 'lucide-react';
import { ErrorActionProps } from '@/lib/errorActions';

interface Plybook {
  id: string;
  name: string;
  version: string;
  status: string;
}

export function QuickPlybookAssign({ workOrder, onSuccess, onCancel }: ErrorActionProps) {
  const { toast } = useToast();
  const [plybooks, setPlybooks] = useState<Plybook[]>([]);
  const [selectedPlybookId, setSelectedPlybookId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchPlybooks = async () => {
      const { data, error } = await api
        .from('plybooks')
        .select('id, name, version, status')
        .in('status', ['approved', 'active', 'draft'])
        .order('name');

      if (!error && data) {
        setPlybooks(data);
      }
      setLoading(false);
    };
    fetchPlybooks();
  }, []);

  const handleAssign = async () => {
    if (!selectedPlybookId) return;

    setSubmitting(true);
    try {
      const { error } = await api
        .from('work_orders')
        .update({ plybook_id: selectedPlybookId })
        .eq('id', workOrder.id);

      if (error) throw error;

      toast({
        title: 'Plybook asignado',
        description: `Plybook asignado a ${workOrder.order_number}`,
      });

      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo asignar el plybook',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Asignar Plybook
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Orden de trabajo</p>
            <p className="font-semibold">{workOrder.order_number}</p>
            {workOrder.product_name && (
              <p className="text-sm text-muted-foreground">{workOrder.product_name}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Selecciona un plybook</label>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Cargando plybooks...
              </div>
            ) : (
              <Select value={selectedPlybookId} onValueChange={setSelectedPlybookId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar plybook" />
                </SelectTrigger>
                <SelectContent>
                  {plybooks.map((pb) => (
                    <SelectItem key={pb.id} value={pb.id}>
                      <div className="flex items-center gap-2">
                        <span>{pb.name}</span>
                        <Badge variant="outline" className="text-xs">v{pb.version}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={handleAssign} disabled={!selectedPlybookId || submitting}>
            {submitting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Asignar y Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
